import {dataTestId} from '@hconnect/uikit'
import {Loader} from '@hconnect/uikit/src/lib2'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import {
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography
} from '@mui/material'
import {KilnMetricStatus} from '@widgets/kms'
import React, {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {useUrlParam} from '../../../../../shared/hooks/useUrlParam'
import {ReactComponent as GroupsEmptyState} from '../../../../shared/icons/groups_empty_state.svg'
import {UserSignalGroup, UserSignalGroupsConfig} from '../../../../shared/types/sensor.types'
import {usePlantMonitoring} from '../../../context/PlantMonitoringContext'
import {useGetGroupsData} from '../../../hooks/useGetGroupsData'
import {useSignalGroupAlarms} from '../../../hooks/useSignalGroupAlarms'

import {AddGroupModal} from './AddGroupModal'

const GroupsEmpty = ({
  setAddGroupModalOpen
}: {
  setAddGroupModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const {t} = useTranslation()

  return (
    <Box px={3} sx={{display: 'flex', justifyContent: 'center', margin: '32px'}}>
      <Box sx={{textAlign: 'center'}}>
        <GroupsEmptyState />
        <Typography variant={'h1'} mt={4} mb={4}>
          {t('groups.noGroupsAvailable')}
        </Typography>
        <Typography sx={{color: '#54708C'}} mb={4}>
          {t('groups.createFirstGroup')}
        </Typography>
        <Button
          variant={'contained'}
          startIcon={<AddIcon />}
          onClick={() => setAddGroupModalOpen(true)}
        >
          {t('groups.newGroup')}
        </Button>
      </Box>
    </Box>
  )
}

export const Groups = ({config}: {config: UserSignalGroupsConfig}) => {
  const {t} = useTranslation()
  const {userGroups, isPending} = useGetGroupsData()
  const [addGroupModalOpen, setAddGroupModalOpen] = useState(false)
  const plantId = useUrlParam('plantId')

  const {
    state: {activeGroup},
    actions
  } = usePlantMonitoring()

  const mergedGroups = useMemo(() => {
    if (!config?.predefinedGroups) return userGroups
    return [...config.predefinedGroups, ...userGroups]
  }, [config?.predefinedGroups, userGroups])

  const filteredGroups = useMemo(() => {
    return mergedGroups
      .filter((group) => group.plantId === plantId)
      .filter((group) => group.masterUpmId === config.masterUpmId)
  }, [config.masterUpmId, mergedGroups, plantId])

  const isEmpty = useMemo(
    () => !filteredGroups?.filter((item) => item.plantId === plantId).length,
    [filteredGroups, plantId]
  )

  if (isPending) {
    return <Loader />
  }

  const handleGroupItemClick = (group: UserSignalGroup) => actions.selectSignalGroup(group)

  const isActive = (name: string) => activeGroup === name

  return (
    <Box>
      <Stack
        direction="column"
        justifyContent="space-between"
        {...dataTestId('pm-groups-menu')}
        alignItems={'left'}
        gap={2}
      >
        <Box display="flex" flexDirection="column" gap={1}>
          {isEmpty ? (
            <GroupsEmpty setAddGroupModalOpen={setAddGroupModalOpen} />
          ) : (
            <Box>
              {!config?.disableEdit && (
                <Box px={3}>
                  {activeGroup ? (
                    <Button
                      variant={'outlined'}
                      color={'primary'}
                      startIcon={<EditIcon />}
                      onClick={() => setAddGroupModalOpen(true)}
                      fullWidth
                    >
                      {t('groups.editGroup')}
                    </Button>
                  ) : (
                    <Button
                      variant={'outlined'}
                      color={'secondary'}
                      startIcon={<AddIcon />}
                      onClick={() => setAddGroupModalOpen(true)}
                      fullWidth
                    >
                      {t('groups.newGroup')}
                    </Button>
                  )}
                </Box>
              )}
              <Box mt={2}>
                <List>
                  {filteredGroups.map((group) => (
                    <SignalGroup
                      key={group.name}
                      isActive={isActive(group.name)}
                      groupData={group}
                      onClick={() => handleGroupItemClick(group)}
                    />
                  ))}
                </List>
              </Box>
            </Box>
          )}
          {addGroupModalOpen && (
            <AddGroupModal
              userGroups={filteredGroups}
              isOpen={addGroupModalOpen}
              handleClose={() => setAddGroupModalOpen(false)}
            />
          )}
        </Box>
      </Stack>
    </Box>
  )
}

const SignalGroup = ({
  isActive,
  groupData,
  onClick
}: {
  isActive: boolean
  onClick: () => void
  groupData: UserSignalGroup
}) => {
  const {alarms, warnings, modelBreaches} = useSignalGroupAlarms(groupData.signals)
  const isWithoutWarnings = !alarms && !warnings && !modelBreaches
  const signalsCount = groupData.signals.length.toString()

  return (
    <Stack>
      <ListItem disablePadding>
        <ListItemButton
          onClick={onClick}
          color={'primary'}
          sx={{
            backgroundColor: isActive ? '#E6F0FB' : 'transparent',
            height: '60px',
            padding: 1,
            paddingLeft: 3,
            paddingRight: 3
          }}
        >
          <ListItemText primary={groupData.name} />
          <Stack direction={'row'} gap={1} alignItems={'flex-end'}>
            <KilnMetricStatus status={'SignalsCount'} value={signalsCount} />
            {isWithoutWarnings && <KilnMetricStatus status={'Ok'} value={signalsCount} />}
            {alarms > 0 && <KilnMetricStatus status={'Alarm'} value={alarms?.toString()} />}
            {warnings > 0 && <KilnMetricStatus status={'Warning'} value={warnings?.toString()} />}
            {modelBreaches > 0 && (
              <KilnMetricStatus status={'ModelBreach'} value={modelBreaches?.toString()} />
            )}
          </Stack>
        </ListItemButton>
      </ListItem>
      <Divider variant="middle" sx={{mx: 4}} />
    </Stack>
  )
}
